import type { AppThemeConfig } from "@src/interfaces/theme-config.interface";

const ABLETO_PURPLE_GRAYSCALE_FILTER =
  "grayscale(1) sepia(0.3) hue-rotate(245deg) saturate(1.35) brightness(0.95)";

/**
 * AbleTo-specific theme overrides layered on top of the base Optum theme.
 *
 * Notes for maintainers:
 * - Only include deltas here (tokens/assets that differ from the default experience).
 * - Any token not listed here falls back to the base theme value.
 * - Asset paths here are consumed by route/component-level theme-aware rendering.
 */
export const ABLETO_THEME_CONFIG: AppThemeConfig = {
  theme: {
    colors: {
      // Brand ramp used across primary interactive elements.
      "core.color.brand.10": "#F3E8F0",
      "core.color.brand.20": "#E5CADE",
      "core.color.brand.60": "#D49FCA",
      "core.color.brand.70": "#C177B6",
      "core.color.brand.80": "#A94E99",
      "core.color.brand.90": "#913E84",
      "core.color.brand.100": "#85356F",
      "core.color.brand.110": "#662955",
      "core.color.brand.120": "#491D3D",
      "core.color.brand.130": "#2C1125",

      // Neutral/surface alignment with AbleTo look and feel.
      "core.color.warm-white.10": "#F7F5EF",

      // Success color used for confirmation states and accents.
      "core.color.green.100": "#2B7D82",

      // Interactive semantic overrides for hover/active behavior.
      "web.semantic.color.surface.interactive.standards.hover.selected.secondary":
        "$core.color.brand.10",
      "web.semantic.color.surface.interactive.standards.hover.default.secondary":
        "$core.color.brand.10",
      "web.semantic.color.border.interactive.focus.regular": "$core.color.brand.100",
      "web.semantic.color.surface.interactive.standards.active.default.secondary":
        "$core.color.brand.20",
      "web.semantic.color.surface.interactive.standards.active.selected.secondary":
        "$core.color.brand.20",

      // Override pagination tokens used in FindProvider results pagination to align with brand colors.
      "pagination.color.text.label.navigation.rest": "$core.color.brand.100",
      "pagination.color.text.label.navigation.hover": "$core.color.brand.110",
      "pagination.color.icon.navigation.rest": "$core.color.brand.100",
      "pagination.color.icon.navigation.hover": "$core.color.brand.110",

      // Custom in-app tokens for consistent styling of non-standard elements.
      "app.color.find.member.id.modal.divider": "$core.color.brand.10",
      "app.color.provider.list.loading.spinner": "$core.color.brand.80",
      "app.color.slot.selected": "$core.color.brand.70",
      "app.color.find.member.id.modal.close.button": "$core.color.neutral.80",
      "app.color.top.background": "$core.color.brand.20",
      "app.color.find.provider.dropdown.border": "$core.color.brand.100",
      "app.color.find.provider.dropdown.text": "$core.color.brand.100",
      "app.color.find.provider.dropdown.icon": "$core.color.brand.100",

      //disclaimer message color
      "app.color.patient.screener.disclaimer.text": "$core.color.green.100",

      //get started section text color
      "app.color.get.started.new.patient.text": "$core.color.brand.100",
      "app.color.get.started.divider": "#504F4A",
    },
  },
  assets: {
    illustrations: {
      digital_conversation: {
        src: "/illustrations/digital_conversation.svg",
        alt: "Digital conversation illustration",
        style: {
          width: "304px",
          height: "auto",
          filter: ABLETO_PURPLE_GRAYSCALE_FILTER,
        },
      },
      clinical_exam_1: {
        src: "/illustrations/clinical_exam_1.svg",
        alt: "Clinical exam illustration",
        style: {
          width: "208.53px",
          height: "auto",
          filter: ABLETO_PURPLE_GRAYSCALE_FILTER,
        },
      },
      digital_insurance_card: {
        src: "/illustrations/digital_insurance_card.svg",
        alt: "Digital insurance card illustration",
        style: {
          width: "304px",
          height: "auto",
          transform: "rotateY(180deg)",
          filter: ABLETO_PURPLE_GRAYSCALE_FILTER,
        },
      },
      lighthouse: {
        src: "/illustrations/lighthouse.svg",
        alt: "Lighthouse illustration",
        style: {
          filter: ABLETO_PURPLE_GRAYSCALE_FILTER,
        },
      },
      // Example of explicit hide: keep default fallback behavior for other illustrations,
      // but intentionally hide this one for AbleTo.
      telescope: {
        hidden: true,
      },
      // Keep these legacy Optum-only illustrations hidden in AbleTo.
      hot_air_balloon: {
        hidden: true,
      },
      interactive_counseling: {
        hidden: true,
      },
    },
    header: {
      title: {
        hidden: true,
      },
      logo: {
        src: "/themes/ableto/logo.svg",
        alt: "AbleTo Logo",
        style: { width: "150px", height: "auto", padding: "10px 0" },
      },
    },
    icons: {
      // Theme-specific icon assets used when components opt into themed sources.
      phone: {
        src: "/themes/ableto/icons/phone.svg",
        alt: "Phone icon",
        style: {
          width: "16px",
          height: "16px",
        },
      },
      mental_health: {
        src: "/themes/ableto/icons/mental_health.svg",
        alt: "Mental health icon",
        style: {}, // Style intentionally left blank as this icon is used at multiple sizes across the app
      },
      calendar: {
        src: "/themes/ableto/icons/calendar.svg",
        alt: "Calendar icon",
        style: {
          width: "40px",
          height: "40px",
        },
      },
      location_pin: {
        src: "/themes/ableto/icons/location_pin.svg",
        alt: "Location pin icon",
        style: {
          width: "40px",
          height: "40px",
        },
      },
      conversation: {
        src: "/themes/ableto/icons/conversation.svg",
        alt: "Conversation icon",
        style: {
          width: "40px",
          height: "40px",
        },
      },
      desktop: {
        src: "/themes/ableto/icons/desktop.svg",
        alt: "Desktop icon",
        style: {
          width: "40px",
          height: "40px",
        },
      },
      brain: {
        src: "/themes/ableto/icons/brain.svg",
        alt: "Brain icon",
        style: {
          width: "40px",
          height: "40px",
        },
      },
      calendar_onetonedarkcircle: {
        src: "/themes/ableto/icons/calendar_onetonedarkcircle.svg",
        alt: "Calendar icon",
        style: {
          width: "76px",
          height: "76px",
          padding: "0px",
        },
      },
      magnifying_glass_twotonedarkcircle: {
        src: "/themes/ableto/icons/magnifying_glass_twotonedarkcircle.svg",
        alt: "Magnifying glass icon",
        style: {
          width: "72px",
          height: "72px",
        },
      },
    },
  },
  content: {
    pages: {
      appointmentConfirmed: {
        supportPhoneText: {
          patient:
            "If you need to add additional services, cancel or reschedule the patient's existing appointment, please call {phoneNumber}",
          self: "If you need to add additional services, cancel or reschedule your existing appointment, please call {phoneNumber}",
        },
      },
      findProvider: {
        noResultsHelpText: "Call {phoneNumber} to finish setting up your appointment.",
        noResultsAppointmentRequestText: {
          patient:
            "Call {phoneNumber} to finish setting up the patient's appointment, or click below to submit an appointment request",
          self: "Call {phoneNumber} to finish setting up your appointment, or click below to submit an appointment request",
        },
      },
      refer: {
        headingText: "Welcome to the referral portal for AbleTo",
        metaTitle: "Welcome to the referral portal for AbleTo",
      },
    },
  },
  // providerGroupId: #,
};
